/**
 * Soft!=Live Page203 — Settings Credential Fabric residual liquid (Page110/54 + dual).
 * Mount: /settings/fabric
 * Buses (contract ops — never painted in operator DOM).
 * Soft residual deleted:
 *  - invent seat chips while posture loading
 *  - invent open desk for non-SA
 *  - invent share material / DEK / sealed_secret
 *  - invent rotation execute
 *  - path /operation labels painted in DOM
 * Marker: Page203 Soft residual
 */
import { useState } from "react";
import { html, F, Ico, KV, Card, Alert } from "./ui.js";
import { path, useApi } from "./api.js";
import {
  useFabricReceipts,
  useFabricRotation,
  useFabricStatus,
} from "./fabric-api.js";

/** Soft!=Live Page203: missing contract ops must not crash the desk. */
function safeFabricPath(op) {
  try {
    return path(op);
  } catch (_e) {
    if (op === "get__settings_fabric_status") return "/settings/fabric/status";
    return "";
  }
}

/** Path-free operator copy for Fabric desk. */
export function softFabricSettingsErrorOf(err) {
  const n = String(err == null ? "" : err).trim();
  const low = n.toLowerCase();
  if (!low) return "That action could not be completed.";
  if (/\b403\b|forbidden|seat_gated|not_authorized|denied|insufficient|capability/.test(low)) {
    return "Your account cannot open credential fabric tools. Ask a platform admin if you need access.";
  }
  if (/\b401\b|unauthorized|missing bearer|session/.test(low)) {
    return "Sign in again to continue.";
  }
  if (/\b404\b|not registered|disabled|not.?armed/.test(low)) {
    return "Credential fabric is not mounted on this deployment yet.";
  }
  if (/\b422\b|\b400\b|invalid|required/.test(low)) {
    return "That fabric request was not accepted.";
  }
  if (/\b429\b|too many/.test(low)) {
    return "Too many tries - wait a moment, then retry.";
  }
  if (/\b503\b|unavailable|network|timeout|failed to fetch/.test(low)) {
    return "Fabric services did not answer. Try again shortly.";
  }
  if (/\/api\/|\/credential-fabric|\/settings\/fabric|GET |POST |status_path|http/.test(low)) {
    return "That action could not be completed.";
  }
  if (n.length > 160) return n.slice(0, 157) + "...";
  return n;
}

/** Pure: seat chips from Live Settings Fabric posture (Page203). */
export function fabricSettingsSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const paintMax = Number(lim.receipt_paint_max);
  return {
    surveyAllowed: p.survey_allowed === true,
    receiptsAllowed: p.receipts_allowed === true,
    rotationPreviewAllowed: p.rotation_preview_allowed === true,
    rotationExecuteAllowed: p.rotation_execute_allowed === true,
    reReadAllowed: p.re_read_allowed === true,
    saFloor: p.sa_floor === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    postureLabel: String(p.posture_label || "").trim() || null,
    softTitle: String(p.soft_title || "").trim() || null,
    softMessage: String(p.soft_message || "").trim() || null,
    enabled: p.enabled === true,
    armed: p.armed === true,
    receiptPaintMax:
      Number.isFinite(paintMax) && paintMax > 0 ? Math.floor(paintMax) : 40,
  };
}

/** Pure: posture KPI view from status pack. */
export function fabricPostureOf(status) {
  const s = status && typeof status === "object" ? status : {};
  const rot = s.rotation || {};
  return {
    backend: String(s.secret_backend || "—"),
    fabricOn: !!s.fabric_enabled,
    cdp: !!s.cdp_url_configured,
    credDsn: !!s.cred_dsn_dedicated,
    redisNonce: !!s.redis_nonce_required,
    hosts: Array.isArray(s.allow_hosts) ? s.allow_hosts : [],
    rotReady: !!rot.ready,
    rotReason: String(rot.reason || ""),
    shareViolation: !!rot.api_share_a_violation,
    claims: Array.isArray(s.claims) ? s.claims : [],
    purposes: s.purposes && typeof s.purposes === "object" ? s.purposes : {},
  };
}

/** Pure: receipt rows (clamped). */
export function fabricReceiptRowsOf(receipts, paintMax) {
  const max = Number(paintMax) > 0 ? Math.floor(Number(paintMax)) : 40;
  const rows = Array.isArray(receipts) ? receipts.slice(0, max) : [];
  return rows.map((r, i) => ({
    key: String((r.tenant_id || "t") + "-" + (r.seq != null ? r.seq : i)),
    tenant: String(r.tenant_id || "—"),
    seq: r.seq != null ? String(r.seq) : "—",
    outcome: String(r.outcome || "—"),
    hash: String(r.hash_prefix || "—"),
    prev: String(r.prev_hash_prefix || ""),
    intent: String(r.intent_compact || ""),
    chainOk: !!r.chain_ok,
  }));
}

export function FabricSettingsDesk({ toast }) {
  const gate = useApi(safeFabricPath("get__settings_fabric_status"));
  const post = gate.data || {};
  // Fail-closed while loading - seats fail-closed.
  const seat = fabricSettingsSeatOf(gate.loading || !gate.data ? {} : post);

  const st = useFabricStatus(seat.surveyAllowed);
  const rc = useFabricReceipts(seat.receiptsAllowed);
  const rot = useFabricRotation();
  const [preview, setPreview] = useState(null);

  const posture = fabricPostureOf(st.status);
  const receipts = fabricReceiptRowsOf(rc.receipts, seat.receiptPaintMax);
  const notRegistered = !!(st.error && /not registered|404/i.test(st.error || ""));

  const softTitle = seat.softTitle || seat.postureLabel || "Credential fabric awareness";
  const softLede = seat.softMessage || seat.postureLabel || (
    "Review ceremony health. Secret material never appears here. "
    + "Execute never runs from this desk."
  );

  const reload = () => {
    if (!seat.reReadAllowed) {
      toast && toast("Refresh is not available for this account", "warn");
      return;
    }
    gate.reload && gate.reload();
    st.reload && st.reload();
    rc.reload && rc.reload();
    toast && toast("Fabric status refreshed", "go");
  };

  const runPreview = async () => {
    if (!seat.rotationPreviewAllowed) {
      toast && toast("Rotation preview is not available on this seat", "warn");
      return;
    }
    if (seat.rotationExecuteAllowed) {
      toast && toast("Execute is never available from the console desk", "warn");
      return;
    }
    const out = await rot.preview();
    if (!out) {
      toast && toast(softFabricSettingsErrorOf(rot.error) || "Rotation preview was refused", "bad");
      return;
    }
    setPreview(out);
    toast && toast("Rotation preview ready - nothing was performed", "go");
  };

  if (!gate.loading && !seat.enabled) {
    return html`<div className="fabric-desk fabric-desk--liquid"
      data-cmc-page="page110-fabric" data-cmc-fabric="page54" data-cmc-residual="page203-fabric">
      <${Alert} tone="warn" title=${softTitle}
        say=${softLede}/>
    </div>`;
  }

  if (!gate.loading && !seat.saFloor) {
    return html`<div className="fabric-desk fabric-desk--liquid"
      data-cmc-page="page110-fabric" data-cmc-fabric="page54" data-cmc-residual="page203-fabric">
      <${Alert} tone="info" title=${softTitle}
        say=${softLede}/>
    </div>`;
  }

  if (st.forbidden) {
    return html`<div className="fabric-desk fabric-desk--liquid"
      data-cmc-page="page110-fabric" data-cmc-fabric="page54" data-cmc-residual="page203-fabric">
      <${Alert} tone="info" title="Platform capability required"
        say="Credential fabric awareness stays with platform AI admin or superadmin. This seat stays sealed."/>
    </div>`;
  }

  return html`<div className="fabric-desk fabric-desk--liquid"
    data-cmc-page="page110-fabric" data-cmc-fabric="page54" data-cmc-residual="page203-fabric">
    <div className="fabric-desk__rail">
      <div>
        <p className="eyebrow">Settings · Credential Fabric</p>
        <h2 className="fabric-desk__title">${softTitle}</h2>
        <p className="note">${softLede}</p>
      </div>
      <div className="btn-row fabric-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm" onClick=${reload}
          disabled=${st.loading || rc.loading || !seat.reReadAllowed}>
          <${Ico} d="refresh" w=${13}/> Refresh
        </button>
      </div>
    </div>

    <${Alert} tone="info" title="Fabric survey and rotation preview"
      say=${`survey=${seat.surveyAllowed ? "open" : "sealed"} · preview=${seat.rotationPreviewAllowed ? "open" : "sealed"} · execute=never · ${seat.role || "—"}`}/>

    ${notRegistered
      ? html`<${Alert} tone="bad" title="Fabric routes not mounted"
          say="This deployment has not registered the fabric product yet."/>`
      : null}
    ${st.error && !st.forbidden && !notRegistered
      ? html`<${Alert} tone="warn" title="Could not load fabric status"
          say=${softFabricSettingsErrorOf(st.error)}/>`
      : null}

    <div className="fabric-desk__kpis">
      <div className="fabric-desk__kpi" data-on=${posture.fabricOn ? "1" : "0"}>
        <span>DDL flag</span>
        <b>${!seat.surveyAllowed ? "sealed" : (st.loading && !st.status ? "…" : (posture.fabricOn ? "on" : "off"))}</b>
        <em>survey</em>
      </div>
      <div className="fabric-desk__kpi" data-on=${posture.backend !== "—" ? "1" : "0"}>
        <span>Secret backend</span>
        <b>${!seat.surveyAllowed ? "sealed" : (st.loading && !st.status ? "…" : posture.backend)}</b>
        <em>honesty</em>
      </div>
      <div className="fabric-desk__kpi" data-on=${posture.cdp ? "1" : "0"}>
        <span>Ceremony host</span>
        <b>${!seat.surveyAllowed ? "—" : (posture.cdp ? "set" : "missing")}</b>
        <em>config</em>
      </div>
      <div className="fabric-desk__kpi" data-on=${posture.shareViolation ? "0" : "1"}>
        <span>Share split</span>
        <b>${!seat.surveyAllowed ? "—" : (posture.shareViolation ? "VIOLATION" : "clean")}</b>
        <em>must be clean</em>
      </div>
    </div>

    <div className="fabric-desk__grid">
      <${Card} ai title="Fabric posture"
          meta=${!seat.surveyAllowed ? "sealed" : posture.backend}
          sub="Empty or off is honesty when fabric is not armed. Secret material never appears.">
        ${!seat.surveyAllowed
          ? html`<${Alert} tone="warn" title="Survey sealed"
              say="Posture KPIs stay closed until the server opens this seat."/>`
          : st.loading && !st.status
          ? html`<p className="note">Loading status…</p>`
          : st.status
            ? html`<${F}>
                <${KV} k="Secret backend" v=${posture.backend}/>
                <${KV} k="Fabric DDL flag" v=${posture.fabricOn ? "on" : "off"}
                  tone=${posture.fabricOn ? "go" : "dim"}/>
                <${KV} k="Ceremony host configured" v=${posture.cdp ? "yes" : "no"}
                  tone=${posture.cdp ? "go" : "warn"}/>
                <${KV} k="Credential store dedicated" v=${posture.credDsn ? "yes" : "no"}
                  tone=${posture.credDsn ? "go" : "warn"}/>
                <${KV} k="Nonce store required" v=${posture.redisNonce ? "fail-closed" : "optional"}/>
                <${KV} k="Allow hosts" v=${posture.hosts.join(", ") || "(none - deny all)"}/>
                <${KV} k="Rotation ready" v=${posture.rotReady
                  ? "yes"
                  : ("no" + (posture.rotReason ? " - " + posture.rotReason : ""))}
                  tone=${posture.rotReady ? "go" : "warn"}/>
                ${posture.shareViolation
                  ? html`<${Alert} tone="bad" title="Share material present on this API process"
                      say="Misconfiguration. Share pieces must live only on the ceremony host. Rotate and scrub API env immediately."/>`
                  : html`<${Alert} tone="go" title="API does not hold share material"
                      say="Catalogue rows may hold credential pointers only. Injection stays on the ceremony host."/>`}
                ${posture.claims.length
                  ? html`<ul className="note fabric-desk__claims">
                      ${posture.claims.map((c) => html`<li key=${c}>${c}</li>`)}
                    </ul>`
                  : null}
              <//>`
            : html`<p className="note">No status pack yet.</p>`}
      <//>

      <${F}>
        <${Card} title="Rotation ceremony"
            sub="Dry preview only. Rewrap happens on the ceremony host; sealed secrets are never rewritten here.">
          ${!seat.rotationPreviewAllowed
            ? html`<${Alert} tone="warn" title="Preview sealed"
                say="Rotation invent stays deleted without a server seat."/>`
            : null}
          <div className="btn-row">
            <button type="button" className="btn btn--ai btn--sm"
              disabled=${rot.busy || !!st.forbidden || notRegistered || !seat.rotationPreviewAllowed}
              onClick=${runPreview}>
              <${Ico} d="spark" w=${13}/>
              ${rot.busy ? "Previewing…" : "Preview rotation"}
            </button>
          </div>
          ${preview
            ? html`<${F}>
                <${KV} k="Ready" v=${preview.ready
                  ? "yes"
                  : ("no - " + (preview.blocked_reason || ""))}
                  tone=${preview.ready ? "go" : "warn"}/>
                <${KV} k="Performed" v=${preview.performed ? "yes" : "no (preview)"}/>
                <${KV} k="Share violation" v=${preview.api_share_a_violation ? "yes" : "no"}
                  tone=${preview.api_share_a_violation ? "bad" : "go"}/>
                <ol className="note fabric-desk__steps">
                  ${(preview.steps || []).map((step) => html`<li key=${step}>${step}</li>`)}
                </ol>
                <p className="note">${preview.note || ""}</p>
              <//>`
            : html`<p className="note">Run preview to load the ceremony steps.</p>`}
        <//>

        <${Card} title="Egress receipts"
            meta=${!seat.receiptsAllowed
              ? "survey sealed"
              : rc.armed === false
              ? "fabric DDL off"
              : (receipts.length + " recent")}
            sub="Hash prefixes only. A broken chain tip never means a key is shown.">
          ${!seat.receiptsAllowed
            ? html`<${Alert} tone="warn" title="Receipts sealed"
                say="Receipt chain invent stays deleted without a survey seat."/>`
            : null}
          ${seat.receiptsAllowed && rc.loading ? html`<p className="note">Loading receipts…</p>` : null}
          ${seat.receiptsAllowed && rc.note ? html`<p className="note">${rc.note}</p>` : null}
          ${seat.receiptsAllowed && rc.error && !rc.note
            ? html`<${Alert} tone="warn" title="Could not load receipts"
                say=${softFabricSettingsErrorOf(rc.error)}/>`
            : null}
          ${seat.receiptsAllowed && !rc.loading && !receipts.length
            ? html`<p className="note">No receipts yet. After ceremony soak egress, tips appear here.</p>`
            : seat.receiptsAllowed
            ? html`<div className="mdl fabric-desk__receipts">
                ${receipts.map((r) => html`
                  <div key=${r.key} className="mdl__r">
                    <span className="mdl__k">${r.outcome}</span>
                    <span className="mdl__t">
                      <b>${r.tenant} · seq ${r.seq}</b>
                      <code>${r.hash}…</code>
                      <em>${r.intent || ""}</em>
                    </span>
                    <span className="mdl__s" data-state=${r.chainOk ? "ready" : "key_missing"}>
                      ${r.chainOk ? "chained" : "incomplete"}
                    </span>
                  </div>`)}
              </div>`
            : null}
        <//>
      <//>
    </div>
  </div>`;
}

export default FabricSettingsDesk;
